import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';

const MFASetup = () => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [step, setStep] = useState('status'); // status, setup, verify, disable

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/auth/mfa/status');
      setMfaStatus(response);
      setStep('status');
    } catch (err) {
      setError('Failed to fetch MFA status');
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    try {
      setError('');
      setLoading(true);
      const response = await apiClient.post('/api/auth/mfa/setup');
      setSetupData(response);
      setBackupCodes(response.backup_codes);
      setStep('setup');
    } catch (err) {
      setError(err.message || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await apiClient.post('/api/auth/mfa/verify-setup', {
        code: verificationCode
      });
      setSuccess('MFA has been enabled successfully!');
      setShowBackupCodes(true);
      setStep('status');
      fetchMFAStatus();
    } catch (err) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!disableCode || !disablePassword) {
      setError('Please enter your code and password');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await apiClient.post('/api/auth/mfa/disable', {
        code: disableCode,
        password: disablePassword
      });
      setSuccess('MFA has been disabled');
      setDisableCode('');
      setDisablePassword('');
      setStep('status');
      fetchMFAStatus();
    } catch (err) {
      setError(err.message || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit code to regenerate backup codes');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const response = await apiClient.post('/api/auth/mfa/backup-codes/regenerate', {
        code: verificationCode
      });
      setBackupCodes(response.backup_codes);
      setShowBackupCodes(true);
      setSuccess('New backup codes generated!');
      setVerificationCode('');
    } catch (err) {
      setError(err.message || 'Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setSuccess('Backup codes copied to clipboard!');
  };

  if (loading && !setupData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Two-Factor Authentication (2FA)
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-400 text-green-700 dark:text-green-300 rounded">
          {success}
        </div>
      )}

      {/* Status View */}
      {step === 'status' && mfaStatus && (
        <div>
          {/* Toggle Switch Row */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-3 ${mfaStatus.mfa_enabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  Two-Factor Authentication
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {mfaStatus.mfa_enabled ? 'Your account is protected' : 'Add extra security to your account'}
                </div>
              </div>
            </div>

            {/* Toggle Switch */}
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mfaStatus.mfa_enabled}
                onChange={() => {
                  if (mfaStatus.mfa_enabled) {
                    setStep('disable');
                  } else {
                    startSetup();
                  }
                }}
                disabled={loading}
                className="sr-only peer"
              />
              <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
            </label>
          </div>

          {mfaStatus.mfa_enabled ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your account is protected with two-factor authentication.
                {mfaStatus.backup_codes_count > 0 && (
                  <span className="ml-1">
                    You have {mfaStatus.backup_codes_count} backup codes remaining.
                  </span>
                )}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setStep('disable')}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition"
                >
                  Disable MFA
                </button>
                <button
                  onClick={() => {
                    setStep('regenerate');
                    setVerificationCode('');
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition"
                >
                  Regenerate Backup Codes
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Add an extra layer of security to your account by enabling two-factor authentication.
                You'll need an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy.
              </p>
              <button
                onClick={startSetup}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition disabled:opacity-50"
              >
                {loading ? 'Setting up...' : 'Enable MFA'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Setup View - Show QR Code */}
      {step === 'setup' && setupData && (
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Step 1: Scan QR Code
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Open your authenticator app and scan this QR code:
            </p>
            <div className="flex justify-center bg-white p-4 rounded-lg inline-block">
              <img src={setupData.qr_code} alt="MFA QR Code" className="w-48 h-48" />
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Can't scan? Enter this code manually:
            </h4>
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded font-mono text-sm break-all">
              {setupData.secret}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Step 2: Enter Verification Code
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Enter the 6-digit code from your authenticator app:
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-32 text-center text-lg tracking-widest dark:bg-gray-700 dark:text-white"
                maxLength={6}
              />
              <button
                onClick={verifySetup}
                disabled={loading || verificationCode.length !== 6}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </div>

          <button
            onClick={() => {
              setStep('status');
              setSetupData(null);
            }}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Disable MFA View */}
      {step === 'disable' && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-white">
            Disable Two-Factor Authentication
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            To disable MFA, enter your current authenticator code and password:
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Authenticator Code
            </label>
            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="Enter code"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <input
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="Enter your password"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={disableMFA}
              disabled={loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition disabled:opacity-50"
            >
              {loading ? 'Disabling...' : 'Disable MFA'}
            </button>
            <button
              onClick={() => setStep('status')}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-md transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes View */}
      {step === 'regenerate' && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-white">
            Regenerate Backup Codes
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Enter your current authenticator code to generate new backup codes.
            This will invalidate all existing backup codes.
          </p>

          <div className="flex gap-3">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-32 text-center text-lg tracking-widest dark:bg-gray-700 dark:text-white"
              maxLength={6}
            />
            <button
              onClick={regenerateBackupCodes}
              disabled={loading || verificationCode.length !== 6}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate New Codes'}
            </button>
          </div>

          <button
            onClick={() => setStep('status')}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Backup Codes Modal */}
      {showBackupCodes && backupCodes.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Save Your Backup Codes
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Store these codes in a safe place. Each code can only be used once.
              You'll need these if you lose access to your authenticator app.
            </p>

            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div key={index} className="font-mono text-sm text-gray-800 dark:text-gray-200">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={copyBackupCodes}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition"
              >
                Copy Codes
              </button>
              <button
                onClick={() => setShowBackupCodes(false)}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition"
              >
                I've Saved Them
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFASetup;
